import { useState } from "react";
import { Bot, ChevronRight, CircleHelp, ShieldCheck, X, Zap, Book } from "lucide-react";
import { API, axios, scoreTone } from "../lib/shared";

// Simulator was previously colocated in App.js — moved here in the 2026-03 cleanup.
// The component is self-contained: it owns its own state and only speaks to
// `/simulator/analyze`, `/simulator/mark-active`, and `/simulator/not-active`.

const CAT_TONE = { active_customer: "green", marketing_lead: "amber", new_lead: "blue" };
const CAT_LABEL = { active_customer: "ACTIVE CUSTOMER", marketing_lead: "MARKETING LEAD", new_lead: "NEW LEAD" };

function Badge({ children, tone = "blue", "data-testid": tid }) {
  return <span data-testid={tid || `badge-${String(children).toLowerCase().replaceAll(" ", "-")}`} className={`badge badge-${tone}`}>{children}</span>;
}

function PageHeader({ eyebrow, title, subtitle }) {
  return <div className="page-header">
    <div><div className="eyebrow">{eyebrow}</div><h1 data-testid="page-header-title">{title}</h1><p>{subtitle}</p></div>
  </div>;
}

export default function Simulator() {
  const [message, setMessage] = useState("Hi, I live in Kharadi and want a high protein non veg plan for lunch and dinner. I want to start Monday.");
  const [result, setResult] = useState(null);
  const [correction, setCorrection] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setCorrection(null);
    const { data } = await axios.post(`${API}/simulator/analyze`, { message });
    setResult({ analysis: data.analysis, classification: data.classification, customer_name: data.customer_name, phone_number: data.phone_number });
  };

  const markActive = async () => {
    if (!result) return;
    setBusy(true); setCorrection(null);
    try {
      const cls = result.classification || {};
      const { data } = await axios.post(`${API}/simulator/mark-active`, {
        message,
        original_category: cls.category,
        original_source: cls.source,
        original_confidence: cls.ai_confidence,
        phone_number: result.phone_number,
      });
      setResult(prev => ({ ...prev, classification: { ...(prev.classification || {}), category: "active_customer", corrected: true, corrected_from: prev.classification?.category, source: "simulator_correction", ai_reasoning: "agent-corrected" } }));
      setCorrection({ status: "success", text: `Marked active_customer · ${data.learner?.added || 0} new keyword(s) learned · ${data.active_customer_captured ? "moved to Active Customers" : "labelled example stored"}` });
    } catch (err) {
      setCorrection({ status: "error", text: err.response?.data?.detail || "Correction failed" });
    } finally { setBusy(false); }
  };

  const markNotActive = async () => {
    if (!result) return;
    setBusy(true); setCorrection(null);
    try {
      const cls = result.classification || {};
      const { data } = await axios.post(`${API}/simulator/not-active`, {
        message,
        original_category: cls.category,
        original_source: cls.source,
        original_confidence: cls.ai_confidence,
        phone_number: result.phone_number,
      });
      setResult(prev => ({ ...prev, classification: { ...(prev.classification || {}), category: "new_lead", corrected: true, corrected_from: "active_customer", source: "simulator_correction_negative", ai_reasoning: "agent-flagged not active" } }));
      setCorrection({ status: "success", text: `Negative example stored · ${data.active_customer_removed ? `removed ${data.active_customer_removed} row(s) from Active Customers` : "no active-customer record to remove"}` });
    } catch (err) {
      setCorrection({ status: "error", text: err.response?.data?.detail || "Correction failed" });
    } finally { setBusy(false); }
  };

  const cls = result?.classification;

  return <>
    <PageHeader eyebrow="SANDBOX / SHADOW MODE" title="Lead simulator" subtitle="Test qualification logic before it touches a customer conversation. Every correction here trains the classifier." />
    <div className="simulator-grid">
      <section className="panel simulator-input">
        <div className="section-title"><Bot size={16} /> Incoming WhatsApp message</div>
        <textarea data-testid="simulator-message-input" value={message} onChange={e => setMessage(e.target.value)} />
        <button data-testid="run-ai-qualification-button" className="button button-primary full" onClick={run}><Zap size={16} /> Run AI qualification</button>
        <div className="scenario-list">
          <span>QUICK SCENARIOS</span>
          {["Hot subscription lead", "B2B office requirement", "Angry delivery complaint", "Sunday delivery to Kothrud"].map((x, i) => <button data-testid={`scenario-${i + 1}-button`} key={x} onClick={() => setMessage(["I want a protein subscription in Kharadi and start tomorrow", "We need 100 daily meals for our office cafeteria", "My delivery is wrong and I need a refund", "Can you deliver a paushtikk thali plan on Sunday in Kothrud?"][i])}>{x}<ChevronRight size={14} /></button>)}
        </div>
      </section>
      <section className="panel sim-result">
        <div className="panel-heading">
          <div><h2>Qualification output</h2><p>Structured internal decision — never shown to the customer</p></div>
          {result && <Badge tone="green">COMPLETE</Badge>}
        </div>
        {result ? <>
          {cls && <div className="classification-card" data-testid="simulator-classification-card">
            <div className="classification-head">
              <small>CLASSIFICATION</small>
              <Badge tone={CAT_TONE[cls.category] || "blue"} data-testid="simulator-classification-category">{CAT_LABEL[cls.category] || String(cls.category || "").toUpperCase()}</Badge>
              {cls.corrected && <Badge tone="green" data-testid="simulator-classification-corrected">CORRECTED</Badge>}
            </div>
            <div className="classification-meta" data-testid="simulator-classification-meta">
              <div><small>Source</small><b data-testid="simulator-classification-source">{cls.source === "llm" ? "LLM (Gemini)" : cls.source === "keyword" ? "Keyword rule" : cls.source || "—"}</b></div>
              <div><small>Confidence</small><b data-testid="simulator-classification-confidence">{cls.ai_confidence != null ? `${Math.round(cls.ai_confidence * 100)}%` : cls.source === "keyword" ? "100% (rule)" : "—"}</b></div>
              <div><small>Intent</small><b>{cls.intent || "—"}</b></div>
              {cls.template_type && <div><small>Template</small><b>{cls.template_type}</b></div>}
            </div>
            {cls.ai_reasoning && <p className="classification-reason" data-testid="simulator-classification-reason">{cls.ai_reasoning}</p>}
            <div className="classification-actions">
              {cls.corrected ? null : cls.category === "active_customer" ? (
                <button data-testid="simulator-not-active-button" className="button button-danger" disabled={busy} onClick={markNotActive} title="Store a negative labelled example and remove any auto-captured active_customer row for this phone.">
                  <X size={13} /> {busy ? "Correcting…" : "Not Active — Correct Classification"}
                </button>
              ) : (
                <button data-testid="simulator-mark-active-button" className="button button-success" disabled={busy} onClick={markActive} title="Store this as a labelled example, add to Active Customers, and mine new keywords from the message.">
                  <ShieldCheck size={13} /> {busy ? "Correcting…" : "Mark as Active Customer"}
                </button>
              )}
            </div>
            {correction && <div className={`classification-notice ${correction.status}`} data-testid="simulator-correction-notice">{correction.text}</div>}
          </div>}
          <div className="result-score"><span>Lead score</span><strong>{result.analysis.lead_score}</strong><Badge tone={scoreTone(result.analysis.lead_score)}>{result.analysis.lead_temperature}</Badge></div>
          <div className="result-grid">{[["Detected intent", result.analysis.intent], ["Sales stage", result.analysis.sales_stage], ["Plan interest", result.analysis.plan_interest], ["Location", result.analysis.location], ["Next best action", result.analysis.next_best_action], ["Confidence", `${Math.round(result.analysis.confidence * 100)}%`]].map(([a, b]) => <div key={a}><small>{a}</small><b>{b}</b></div>)}</div>
          {result.analysis.sunday_serviceable === false && <div className="sunday-flag" data-testid="sunday-unserviceable-flag"><CircleHelp size={15} /> <div><strong>Sunday delivery not available at {result.analysis.location}</strong><small>Per delivery policy — see Knowledge Base → Delivery Policy for the serviceable Sunday areas.</small></div></div>}
          {result.analysis.kb_references && result.analysis.kb_references.length > 0 && <div className="kb-citations" data-testid="kb-citations"><small>KB REFERENCES USED</small>{result.analysis.kb_references.map(ref => <div key={ref.slug} className="kb-citation" data-testid={`kb-citation-${ref.slug}`}><Book size={12} /><b>{ref.title}</b><span>· {ref.why}</span></div>)}</div>}
          <div className="reason-list"><small>SCORING SIGNALS</small>{result.analysis.score_reasons.map(x => <p key={x}>✓ {x}</p>)}</div>
          <div className="draft-response"><small>AI RESPONSE DRAFT</small><p>{result.analysis.reply}</p><Badge tone={result.analysis.human_required ? "red" : "blue"}>{result.analysis.human_required ? "HUMAN REQUIRED" : "SHADOW — AWAITING APPROVAL"}</Badge></div>
        </> : <div className="empty-state"><Bot size={32} /><strong>Ready to qualify</strong><span>Run a scenario to see the full lead decision.</span></div>}
      </section>
    </div>
  </>;
}
